import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";

export default function AdminInvoices() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    customer_email: "", customer_name: "",
    items: [{ description: "SSL Installation", quantity: 1, unit_price: 299.99 }],
    tax_rate: 0, notes: "", status: "sent", due_date: "",
  });

  const load = () => api.get("/admin/invoices").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const setItem = (i, k, v) => {
    const arr = [...form.items];
    arr[i] = { ...arr[i], [k]: k === "description" ? v : parseFloat(v) || 0 };
    setForm({ ...form, items: arr });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { description: "", quantity: 1, unit_price: 0 }] });
  const rmItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/invoices", {
        ...form,
        tax_rate: parseFloat(form.tax_rate) || 0,
        due_date: form.due_date || null,
      });
      toast.success("Invoice created");
      setCreating(false);
      setForm({ customer_email: "", customer_name: "", items: [{ description: "", quantity: 1, unit_price: 0 }], tax_rate: 0, notes: "", status: "sent", due_date: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const updateStatus = async (id, status) => {
    try { await api.patch(`/admin/invoices/${id}/status`, { status }); load(); }
    catch { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try { await api.delete(`/admin/invoices/${id}`); load(); toast.success("Deleted"); }
    catch { toast.error("Failed"); }
  };

  const subtotal = form.items.reduce((s, it) => s + (it.quantity * it.unit_price), 0);
  const tax = subtotal * ((parseFloat(form.tax_rate) || 0) / 100);
  const total = subtotal + tax;

  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Invoices</h1>
          <p className="text-sm text-muted-foreground mt-1">Create and manage customer invoices.</p>
        </div>
        <button onClick={() => setCreating(v => !v)} className="btn-primary !py-2 !px-4 !text-sm" data-testid="admin-invoice-new"><Plus className="h-4 w-4"/> New invoice</button>
      </div>

      {creating && (
        <form onSubmit={submit} className="mt-6 card-elevated p-6 space-y-3" data-testid="admin-invoice-form">
          <div className="grid gap-3 md:grid-cols-2">
            <input required placeholder="Customer name" value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})} data-testid="ai-name" className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />
            <input required type="email" placeholder="Customer email" value={form.customer_email} onChange={(e) => setForm({...form, customer_email: e.target.value})} data-testid="ai-email" className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />
          </div>
          <div className="space-y-2">
            {form.items.map((it, i) => (
              <div key={i} className="grid grid-cols-[1fr_90px_120px_40px] gap-2 items-center">
                <input required placeholder="Description" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`ai-item-desc-${i}`} className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />
                <input required type="number" step="0.01" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} data-testid={`ai-item-qty-${i}`} className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />
                <input required type="number" step="0.01" placeholder="Unit price" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} data-testid={`ai-item-price-${i}`} className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />
                <button type="button" onClick={() => rmItem(i)} className="text-destructive" disabled={form.items.length === 1} aria-label="Remove item"><Trash2 className="h-4 w-4"/></button>
              </div>
            ))}
            <button type="button" onClick={addItem} className="text-sm text-[#1565FF] inline-flex items-center gap-1" data-testid="ai-add-item"><Plus className="h-3.5 w-3.5"/> Add item</button>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <input type="number" step="0.01" placeholder="Tax rate %" value={form.tax_rate} onChange={(e) => setForm({...form, tax_rate: e.target.value})} data-testid="ai-tax" className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />
            <input type="date" value={form.due_date} onChange={(e) => setForm({...form, due_date: e.target.value})} data-testid="ai-due" className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />
            <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})} data-testid="ai-status" className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm">
              <option value="draft">Draft</option><option value="sent">Sent</option><option value="paid">Paid</option>
            </select>
          </div>
          <textarea placeholder="Notes (optional)" rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} data-testid="ai-notes" className="w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />
          <div className="flex items-center justify-between pt-2 border-t border-black/5 dark:border-white/10">
            <div className="text-sm text-muted-foreground">
              Subtotal ${subtotal.toFixed(2)} · Tax ${tax.toFixed(2)} · <span className="font-semibold text-foreground">Total ${total.toFixed(2)}</span>
            </div>
            <button className="btn-primary !py-2 !px-4 !text-sm" data-testid="ai-submit"><Save className="h-4 w-4"/> Create invoice</button>
          </div>
        </form>
      )}

      <div className="mt-6 card-elevated overflow-hidden" data-testid="admin-invoices-table">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-black/[0.02] dark:bg-white/[0.02] text-left">
            <tr><th className="px-4 py-3">Number</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Created</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t border-black/5 dark:border-white/10">
                <td className="px-4 py-3 font-medium">{r.number}</td>
                <td className="px-4 py-3">{r.customer_name}<div className="text-xs text-muted-foreground">{r.customer_email}</div></td>
                <td className="px-4 py-3">${Number(r.total || 0).toFixed(2)}</td>
                <td className="px-4 py-3">
                  <select value={r.status} onChange={(e) => updateStatus(r.id, e.target.value)} data-testid={`admin-invoice-status-${r.id}`} className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-2 py-1 text-xs">
                    <option value="draft">draft</option><option value="sent">sent</option><option value="paid">paid</option><option value="overdue">overdue</option><option value="cancelled">cancelled</option>
                  </select>
                </td>
                <td className="px-4 py-3 text-muted-foreground text-xs">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-4 py-3"><button onClick={() => del(r.id)} className="text-destructive" data-testid={`admin-invoice-del-${r.id}`}><Trash2 className="h-4 w-4"/></button></td>
              </tr>
            ))}
            {rows.length === 0 && (<tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No invoices yet.</td></tr>)}
          </tbody>
        </table>
        </div>
      </div>
    </>
  );
}
